import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import api from '../../services/api';
import { BUCKET_COLOR, Card } from './PaycheckSections';

const BUCKETS = ['needs', 'wants', 'savings'];

/** One bucket: spent against plan, with a marker for where the calendar is. */
function PaceBar({ spent, planned, pace, color }) {
  const share = planned > 0 ? Math.min(1, spent / planned) : 0;
  const over = planned > 0 && spent > planned;
  return (
    <Box sx={{ position: 'relative', height: 8, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', inset: 0, width: `${share * 100}%`, bgcolor: over ? 'error.main' : color }} />
      <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${pace * 100}%`, width: 2, bgcolor: 'text.primary', opacity: 0.5 }} />
    </Box>
  );
}

/**
 * Dashboard card: this month's paycheck plan so far. Shows nothing until the
 * user has saved a plan as budgets for the month.
 */
export default function PaycheckProgressCard() {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const { hasFeature } = useSubscription();
  const navigate = useNavigate();
  const unlocked = hasFeature?.('paycheckPlan');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!unlocked) return undefined;
    let alive = true;
    api.get('/paycheck/progress', { timeout: 15000 })
      .then((res) => { if (alive) setData(res.data?.data || null); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [unlocked]);

  if (!unlocked || !data?.hasPlan) return null;

  return (
    <Card>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.25 }}>
        <Typography fontWeight={800}>{t('paycheck.progress.title', 'Paycheck plan this month')}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t('paycheck.progress.daysLeft', { count: data.daysLeft, defaultValue: '{{count}} days left' })}
        </Typography>
      </Stack>
      <Stack spacing={1.25}>
        {BUCKETS.map((b) => {
          const v = data.buckets[b];
          if (!v || (v.planned <= 0 && v.spent <= 0)) return null;
          return (
            <Box key={b}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: BUCKET_COLOR[b] }}>{t(`paycheck.bucket.${b}`, b)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {b === 'savings'
                    ? t('paycheck.progress.saved', { spent: formatAmount(v.spent), planned: formatAmount(v.planned), defaultValue: '{{spent}} saved of {{planned}}' })
                    : t('paycheck.progress.spent', { spent: formatAmount(v.spent), planned: formatAmount(v.planned), defaultValue: '{{spent}} of {{planned}}' })}
                </Typography>
              </Stack>
              <PaceBar spent={v.spent} planned={v.planned} pace={data.pace} color={BUCKET_COLOR[b]} />
            </Box>
          );
        })}
      </Stack>
      {data.ahead?.length ? (
        <Typography variant="body2" sx={{ mt: 1.5 }} color={data.ahead.some((l) => l.over) ? 'error.main' : 'warning.main'}>
          {t('paycheck.progress.ahead', {
            list: data.ahead.slice(0, 3).map((l) => `${l.name} ${Math.round(l.ratio * 100)}%`).join(', '),
            defaultValue: 'Ahead of plan: {{list}}',
          })}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ mt: 1.5 }} color="success.main">
          {t('paycheck.progress.onPace', 'Every category is on pace.')}
        </Typography>
      )}
      <Button size="small" sx={{ mt: 1, px: 0 }} onClick={() => navigate('/dashboard/paycheck')}>
        {t('paycheck.progress.open', 'Open the plan')}
      </Button>
    </Card>
  );
}
